import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UserProfile } from '@/types';
import { useOutreachTray } from '@/hooks/useOutreachTray';
import { useNewMemberToast } from '@/hooks/useNewMemberToast';
import { useNewMembersFlow } from './useNewMembersFlow';
import { NewMembersCaughtUp, NewMembersSection } from './NewMembersSection';

interface Options {
  canModerate: boolean;
  profile: UserProfile | null;
  trayOpen: boolean;
  openTray: () => void;
  closeTray: () => void;
}

/**
 * Wires New Members into the header's leader tray: live data, the say-hello flow, a
 * ticking "now" for age labels, and the /?newMembers=1 deep link used by notifications.
 */
export function useNewMembersTray({
  canModerate,
  profile,
  trayOpen,
  openTray,
  closeTray,
}: Options) {
  const outreach = useOutreachTray(canModerate);
  useNewMemberToast({
    waiting: outreach.waiting,
    ready: canModerate && !outreach.loading,
    enabled: outreach.config.enabled,
  });
  const flow = useNewMembersFlow({
    leader: profile
      ? {
          uid: profile.uid,
          displayName: profile.displayName,
          outreachAckVersion: profile.outreachAckVersion,
        }
      : null,
    config: outreach.config,
    waiting: outreach.waiting,
  });

  // Ages ("Joined 2 hours ago") are worked out here; refresh while the tray is open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!trayOpen) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [trayOpen]);

  // Notification and email links open the tray: /?newMembers=1&member=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('newMembers') !== '1' || !canModerate) return;
    openTray();
    const next = new URLSearchParams(searchParams);
    next.delete('newMembers');
    next.delete('member');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canModerate, openTray]);

  const waitingCount = outreach.visible ? outreach.waiting.length : 0;

  const newMembersProp =
    outreach.visible && profile
      ? {
          count: outreach.waiting.length,
          content: (
            <NewMembersSection
              waiting={outreach.waiting}
              myId={profile.uid}
              now={now}
              busyId={flow.busyId}
              reminderAfterHours={outreach.config.reminderAfterHours}
              escalateAfterHours={outreach.config.escalateAfterHours}
              onSayHello={(record) => {
                closeTray();
                flow.startHello(record);
              }}
              onShowClaimed={(record, by) => {
                closeTray();
                flow.showClaimed(record, by);
              }}
            />
          ),
          emptyContent: <NewMembersCaughtUp recent={outreach.recent} now={now} />,
        }
      : undefined;

  return {
    /** True when the tray shows New members too (it is then called "Needs a look"). */
    combined: outreach.visible,
    waitingCount,
    newMembersProp,
    flowElement: flow.element,
  };
}
